import type { WorkspaceSettingsQuery } from "@src/__generated__/urql-graphql";
import { CompaniesSettingsState } from "@src/stores/forms/SettingsModalState/CompaniesSettingsState";
import type { SettingsModalStore } from "@src/stores/SettingsModalStore";
import { FormState } from "formstate";
import type { WorkspaceSettings } from "../../models/WorkspaceSettings";
import { ExpenseInboxIntegrationState } from "./expense-inbox-integration-state";
import { FinancialSettingsState } from "./FinancialSettingsState";
import { NotificationSettingsState } from "./NotificationSettingsState";
import { PlanningUtilizationState } from "./PlanningUtilizationState";
import { ProjectsSettingsState } from "./ProjectsSettingsState";
import { PipedriveSettingsState } from "./pipedrive-settings-state";
import { SmtpSettingsState } from "./SmtpSettingsState";
import { TasksSettingsState } from "./TasksSettingsState";
import { UserManagementState } from "./UserManagementState";

export class SettingsModalState {
  store: SettingsModalStore;
  notificationsState: NotificationSettingsState;
  tasksState: TasksSettingsState;
  companiesState: CompaniesSettingsState;
  financialState: FinancialSettingsState;
  userManagementState: UserManagementState;
  planningUtilizationState: PlanningUtilizationState;
  projectsState: ProjectsSettingsState;
  pipedriveState: PipedriveSettingsState;
  expenseInboxIntegrationState: ExpenseInboxIntegrationState;
  smtpState: SmtpSettingsState;

  form: FormState<{
    notifications: typeof NotificationSettingsState.prototype.form;
    tasks: typeof TasksSettingsState.prototype.form;
    companies: typeof CompaniesSettingsState.prototype.form;
    financial: typeof FinancialSettingsState.prototype.form;
    userManagement: typeof UserManagementState.prototype.form;
    planningUtilization: typeof PlanningUtilizationState.prototype.form;
    projects: typeof ProjectsSettingsState.prototype.form;
    pipedrive: typeof PipedriveSettingsState.prototype;
    smtp: typeof SmtpSettingsState.prototype.form;
    // expenseInboxIntegration: typeof ExpenseInboxIntegrationState.prototype.form;
  }>;

  constructor(store: SettingsModalStore) {
    this.store = store;
    this.notificationsState = new NotificationSettingsState(store);
    this.tasksState = new TasksSettingsState();
    this.companiesState = new CompaniesSettingsState();
    this.financialState = new FinancialSettingsState();
    this.userManagementState = new UserManagementState(store);
    this.planningUtilizationState = new PlanningUtilizationState();
    this.projectsState = new ProjectsSettingsState();
    this.pipedriveState = new PipedriveSettingsState();
    this.expenseInboxIntegrationState = new ExpenseInboxIntegrationState();
    this.smtpState = new SmtpSettingsState(store);

    this.form = new FormState({
      notifications: this.notificationsState.form,
      tasks: this.tasksState.form,
      companies: this.companiesState.form,
      financial: this.financialState.form,
      userManagement: this.userManagementState.form,
      planningUtilization: this.planningUtilizationState.form,
      projects: this.projectsState.form,
      pipedrive: this.pipedriveState,
      smtp: this.smtpState.form,
      // expenseInboxIntegration: this.expenseInboxIntegrationState.form,
    });
  }

  init(settings: WorkspaceSettingsQuery, workspaceSettings: WorkspaceSettings) {
    this.notificationsState.init(settings.WorkspaceNotifications);
    this.tasksState.init(settings.workspaceTaskSettings);
    this.companiesState.init(settings.workspaceCompaniesSettings);
    this.financialState.init(
      settings.workspaceFinancialSettings,
      settings.workspaceAccountingAccountSetting,
      settings.ourWorkBudgetCategories ?? [],
      settings.expenseBudgetCategories ?? [],
      settings.defaultCurrencies ?? [],
      settings.workspaceAccountingCentreSetting,
      settings.workspaceAccountingVatClassificationSetting,
    );
    this.userManagementState.init(settings.workspaceUserManagementSetting);
    this.planningUtilizationState.init(
      settings.updateWorkspacePlanningSettings,
      workspaceSettings.default_planning_view_settings,
    );
    this.projectsState.init(settings.workspaceProjectSettings);
    this.pipedriveState.init(settings.workspacePipedriveIntegration.settings);
    this.expenseInboxIntegrationState.init(
      settings.workspaceAutoExpenseEmailSettings,
    );
    this.smtpState.init(settings.workspaceSmtpSettings);
  }
}
